import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Star, Users, UtensilsCrossed, BarChart3, MessageSquare,
  Shield, LogOut, RefreshCw, FileText, Lock, ChefHat, Plus, Lightbulb
} from "lucide-react";
import { toast } from "sonner";
import AdminChangePassword from "@/components/AdminChangePassword";
import AddRestaurantTab from "@/components/admin/AddRestaurantTab";
import EditRestaurantDialog from "@/components/admin/EditRestaurantDialog";
import RequestsTab from "@/components/admin/RequestsTab";
import PortalAccountsTab from "@/components/admin/PortalAccountsTab";
import OverviewTab from "@/components/admin/OverviewTab";
import { UsersTab, RestaurantsTab } from "@/components/admin/ManagementTabs";
import { PostsTab, ReviewsTab } from "@/components/admin/ModerationTabs";

const API = window.location.origin + "/api";
const axiosConfig = { withCredentials: true };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [posts, setPosts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [portalAccounts, setPortalAccounts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, axiosConfig);
    } catch (error) {
      console.warn("Admin logout request failed (best-effort):", error?.message);
    }
    navigate("/admin");
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, axiosConfig);
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired");
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const fetchUsers = async (search = "") => {
    try {
      const response = await axios.get(`${API}/admin/users?search=${search}`, axiosConfig);
      setUsers(response.data.users);
    } catch (error) {
      toast.error("Failed to fetch users");
    }
  };

  const fetchRestaurants = async (search = "") => {
    try {
      const response = await axios.get(`${API}/admin/restaurants?search=${search}`, axiosConfig);
      setRestaurants(response.data.restaurants);
    } catch (error) {
      toast.error("Failed to fetch restaurants");
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/posts`, axiosConfig);
      setPosts(response.data.posts);
    } catch (error) {
      toast.error("Failed to fetch posts");
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/admin/reviews`, axiosConfig);
      setReviews(response.data.reviews);
    } catch (error) {
      toast.error("Failed to fetch reviews");
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/restaurant-requests`, axiosConfig);
      setRequests(response.data.requests);
      setPendingRequests(response.data.pending_count);
    } catch (error) {
      toast.error("Vorschläge konnten nicht geladen werden");
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      const res = await axios.put(`${API}/admin/restaurant-requests/${requestId}/approve`, {}, axiosConfig);
      toast.success(`„${res.data.restaurant.name}" wurde übernommen – Nutzer wird benachrichtigt`);
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Übernehmen fehlgeschlagen");
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axios.put(`${API}/admin/restaurant-requests/${requestId}/reject`, {}, axiosConfig);
      toast.success("Vorschlag abgelehnt");
      fetchRequests();
    } catch (error) {
      toast.error("Ablehnen fehlgeschlagen");
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!confirm("Diesen Vorschlag endgültig löschen?")) return;
    try {
      await axios.delete(`${API}/admin/restaurant-requests/${requestId}`, axiosConfig);
      toast.success("Vorschlag gelöscht");
      fetchRequests();
    } catch (error) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const fetchPortalAccounts = async () => {
    try {
      const response = await axios.get(`${API}/admin/restaurant-accounts`, axiosConfig);
      setPortalAccounts(response.data.accounts);
    } catch (error) {
      toast.error("Portal-Konten konnten nicht geladen werden");
    }
  };

  const handleVerifyPortal = async (accountId, verify) => {
    try {
      await axios.put(`${API}/admin/restaurant-accounts/${accountId}/${verify ? 'verify' : 'revoke'}`, {}, axiosConfig);
      toast.success(verify ? "Restaurant freigeschaltet!" : "Freischaltung entzogen");
      fetchPortalAccounts();
    } catch (error) {
      toast.error("Aktion fehlgeschlagen");
    }
  };

  const handleDeletePortalAccount = async (accountId) => {
    if (!confirm("Dieses Restaurant-Konto endgültig löschen (ablehnen)?")) return;
    try {
      await axios.delete(`${API}/admin/restaurant-accounts/${accountId}`, axiosConfig);
      toast.success("Konto gelöscht");
      fetchPortalAccounts();
    } catch (error) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Are you sure you want to delete this user and all their data?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, axiosConfig);
      toast.success("User deleted");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/admin/posts/${postId}`, axiosConfig);
      toast.success("Post deleted");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, axiosConfig);
      toast.success("Review deleted");
      fetchReviews();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "users") fetchUsers();
    if (tab === "restaurants") fetchRestaurants();
    if (tab === "posts") fetchPosts();
    if (tab === "reviews") fetchReviews();
    if (tab === "portal") fetchPortalAccounts();
    if (tab === "requests") fetchRequests();
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6 text-primary fill-primary" />
            <span className="text-xl font-serif text-primary">Étoile</span>
            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
              <Shield className="w-3 h-3" /> Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPasswordDialog(true)}
              className="border-border text-muted-foreground hover:text-foreground"
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4 mr-2" /> Passwort ändern
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-muted-foreground">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="bg-card border border-border mb-8">
            <TabsTrigger value="overview" className="data-[state=active]:bg-primary/20" data-testid="admin-overview-tab">
              <BarChart3 className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-primary/20" data-testid="admin-users-tab">
              <Users className="w-4 h-4 mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger value="restaurants" className="data-[state=active]:bg-primary/20" data-testid="admin-restaurants-tab">
              <UtensilsCrossed className="w-4 h-4 mr-2" /> Restaurants
            </TabsTrigger>
            <TabsTrigger value="add-restaurant" className="data-[state=active]:bg-primary/20" data-testid="admin-add-restaurant-tab">
              <Plus className="w-4 h-4 mr-2" /> Restaurant hinzufügen
            </TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-primary/20" data-testid="admin-requests-tab">
              <Lightbulb className="w-4 h-4 mr-2" /> Vorschläge
              {pendingRequests > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-yellow-500/30 text-yellow-500 text-xs rounded-full" data-testid="pending-requests-badge">
                  {pendingRequests}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="posts" className="data-[state=active]:bg-primary/20" data-testid="admin-posts-tab">
              <MessageSquare className="w-4 h-4 mr-2" /> Posts
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-primary/20" data-testid="admin-reviews-tab">
              <FileText className="w-4 h-4 mr-2" /> Reviews
            </TabsTrigger>
            <TabsTrigger value="portal" className="data-[state=active]:bg-primary/20" data-testid="admin-portal-tab">
              <ChefHat className="w-4 h-4 mr-2" /> Portal
            </TabsTrigger>
          </TabsList>

          <TabsContent value="portal">
            <PortalAccountsTab accounts={portalAccounts} onVerify={handleVerifyPortal} onDelete={handleDeletePortalAccount} />
          </TabsContent>

          <TabsContent value="overview">
            <OverviewTab stats={stats} />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab
              users={users}
              searchTerm={searchTerm}
              onSearch={(v) => { setSearchTerm(v); fetchUsers(v); }}
              onView={(userId) => navigate(`/admin/users/${userId}`)}
              onDelete={handleDeleteUser}
            />
          </TabsContent>

          <TabsContent value="restaurants">
            <RestaurantsTab restaurants={restaurants} onSearch={fetchRestaurants} onEdit={setEditingRestaurant} />
          </TabsContent>

          <TabsContent value="requests">
            <RequestsTab requests={requests} onApprove={handleApproveRequest} onReject={handleRejectRequest} onDelete={handleDeleteRequest} />
          </TabsContent>

          <TabsContent value="add-restaurant">
            <AddRestaurantTab onCreated={() => { fetchStats(); fetchRestaurants(); }} />
          </TabsContent>

          <TabsContent value="posts">
            <PostsTab posts={posts} onDelete={handleDeletePost} />
          </TabsContent>

          <TabsContent value="reviews">
            <ReviewsTab reviews={reviews} onDelete={handleDeleteReview} />
          </TabsContent>
        </Tabs>
      </main>

      <AdminChangePassword open={showPasswordDialog} onOpenChange={setShowPasswordDialog} />
      <EditRestaurantDialog
        restaurant={editingRestaurant}
        onOpenChange={(open) => { if (!open) setEditingRestaurant(null); }}
        onSaved={() => fetchRestaurants(searchTerm)}
      />
    </div>
  );
};

export default AdminDashboard;
